import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import { useAuth } from "../../context/API_Client";
import FileSaver from "file-saver";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import "../modals/modal.css";
import { toast } from "react-toastify";
import CustomButton from "../../reusableComponents/CustomButton";
import { currentTimeInTimezone, fortmatTimezoneToUTC } from "../utils/utils";
import { customToast } from "../../reusableComponents/customToaster/customToast";
import OrderManagementContext from "../../context/orderManagement.context";

const theme = createTheme({
  components: {
    MuiRadio: {
      styleOverrides: {
        root: {
          color: "rgba(36, 183, 170, 1)",
          height: "16px",
          width: "16px",
          marginRight: "8px",
          marginLeft: "10px",
          "&.Mui-checked": {
            color: "rgba(36, 183, 170, 1)",
            height: "16px",
            width: "16px",
            marginRight: "8px",
          },
        },
      },
    },
  },
});

export default function DownloadModal({ openModal, onClose, toastState, setLoading }) {
  const { filters } = useContext(OrderManagementContext);

  const [fromDate, setFromDate] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedValue, setSelectedValue] = useState("");
  const { getDownloadFile, getDownloadFileWithFilters, merchantSettings } = useAuth();
  const [paymentError, setPaymentError] = useState([]);
  const [timeZone, setTimeZone] = useState("");

  const today = new Date();

  const dates = [
    { key: "Last 15 minutes", value: "last15Minutes" },
    { key: "Last 24 hours", value: "last24Hours" },
    { key: "Last week", value: "lastWeek" },
    { key: "Single date", value: "singleDate" },
    { key: "Date range", value: "dateRange" },
  ];

  const isSingleDateSelected = selectedValue === "Single date";
  const isDateRangeSelected = selectedValue === "Date range";
  const isApplyButtonDisabled =
    !selectedValue ||
    (isSingleDateSelected && !fromDate) ||
    (isDateRangeSelected && (!fromDate || !toDate));
  let selectedDate1, selectedDate2;

  const handleRadioChange = (event) => {
    const value = event.target.value;
    setSelectedValue(value);
    const currentTime = new Date();

    switch (value) {
      case "Last 15 minutes":
        setFromDate(new Date(currentTime.getTime() - 15 * 60 * 1000).toISOString());
        setToDate(currentTime.toISOString());
        break;
      case "Last 24 hours":
        setFromDate(new Date(currentTime.getTime() - 24 * 60 * 60 * 1000).toISOString());
        setToDate(currentTime.toISOString());
        break;
      case "Last week":
        let selectedDate1 = new Date(currentTime);
        let selectedDate2 = new Date(currentTime);
        setFromDate(fortmatTimezoneToUTC('last7Days', selectedDate1, selectedDate2)?.fromDate);
        setToDate(fortmatTimezoneToUTC('last7Days', selectedDate1, selectedDate2)?.toDate);
        break;
      case "Single date":
        setFromDate("");
        setToDate("");
        break;
      case "Date range":
        setFromDate("");
        setToDate("");
        break;
      default:
        setFromDate("");
        setToDate("");
    }
  };

  useEffect(() => {
    switch (selectedValue) {
      case "Single date":
        selectedDate1 = new Date(from);
        selectedDate2 = new Date(from);
        setFromDate(fortmatTimezoneToUTC('singleDate', selectedDate1, selectedDate2)?.fromDate);
        setToDate(fortmatTimezoneToUTC('singleDate', selectedDate1, selectedDate2)?.toDate);
        break;
      case "Date range":
        selectedDate1 = new Date(from);
        selectedDate2 = new Date(to);
        setFromDate(fortmatTimezoneToUTC('dateRange', selectedDate1, selectedDate2)?.fromDate);
        setToDate(fortmatTimezoneToUTC('dateRange', selectedDate1, selectedDate2)?.toDate);
        break;
      default:
        setFromDate("");
        setToDate("");
    }
  }, [from, to]);

  const handleCloseModal = () => {
    onClose();
    setFromDate("");
    setToDate("");
    setSelectedValue("");
  };

  const handleClearAllData = () => {
    setFromDate("");
    setToDate("");
    setSelectedValue("");
  };

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      event.stopPropagation();
    }
  };

  const handleDownload = () => {
    let advFilters = {
      attrName: 'transactionId',
      operator: "containsIgnoreCase",
    }
    let req = {
      fromDate: fromDate,
      toDate: toDate,
      limit: 100,
    };
    getDownloadFileWithFilters(filters, function (data) {
      const contentDisposition = data.headers["content-disposition"];
      const match = contentDisposition.match(/filename="(.+?)"/);
      const contentType = data.headers["content-type"];

      let filename;
      if (match && match[1]) {
        filename = match[1];
      } else {
        filename = "Data.csv";
      }
      const blob = new Blob([data.data], {
        type: contentType,
      });
      setLoading(true)
      onClose();
      FileSaver.saveAs(blob, filename);
      toastState();
    },
      function (errorMsg) {
        setPaymentError(errorMsg);
        customToast("error", "high", errorMsg);
        setLoading(false)
      }
    );
  };

  useEffect(() => {
    merchantSettings(
      function (data) {
        setTimeZone(data?.timeZone);
      },
      function (errorMsg) {
        customToast("error", "high", errorMsg);
      }
    );
  }, []);
  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      onClose();
    }
  };
  return (
    <>
      <Dialog
        open={openModal}
        className="orderManagementModal"
        onKeyDown={handleKeyDown}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "6px",
            padding: "24px 24px 0px 24px",
          }}
        >
          <Typography className="SemiboldTwenty font-black-highest">
            Date
          </Typography>
          <img
            src="/assets/ic_twotone-close.svg"
            alt="cross"
            onClick={() => handleCloseModal()}
            style={{ cursor: "pointer" }}
          />
        </div>

        <Typography
          className="labelRegular font-black-high"
          style={{ padding: "0px 24px" }}
        >
          Select the date for listing the reports
        </Typography>
        <div
          style={{
            display: "flex",
            height: "282px",
            overflowY: "scroll",
            padding: "0px 24px",
          }}
        >
          <div style={{ paddingLeft: "2px" }}>
            <RadioGroup
              value={selectedValue}
              onChange={handleRadioChange}
              name="radio-buttons-group"
              sx={{ marginTop: "20px" }}
            >
              {dates.map((date, index) => (
                <div
                  key={index}
                  style={{ height: "18px", marginBottom: "8px" }}
                >
                  <ThemeProvider theme={theme}>
                    <FormControlLabel
                      className="bodyB2RegularText font-black-highest"
                      value={date.key}
                      control={<Radio size="small" />}
                      label={
                        <span
                          style={{
                            color:
                              selectedValue === date.key
                                ? "inherit"
                                : "#01010273",
                          }}
                        >
                          {date.key}
                        </span>
                      }
                      sx={{ marginBottom: "4px" }}
                    />
                  </ThemeProvider>
                </div>
              ))}
            </RadioGroup>
          </div>
          <div className="singleDateStyle" style={{ marginTop: "24px" }}>
            {isSingleDateSelected && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  format="dd/MM/yyyy"
                  maxDate={today}
                  value={from}
                  onChange={(date) => setFrom(date)}
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
            )}
          </div>
          <div className="singleDateStyle" style={{ marginTop: "24px" }}>
            {isDateRangeSelected && (
              <div style={{ display: "flex" }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    format="dd/MM/yyyy"
                    maxDate={today}
                    value={from}
                    onChange={(date) => setFrom(date)}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    format="dd/MM/yyyy"
                    maxDate={today}
                    value={to}
                    onChange={(date) => setTo(date)}
                    renderInput={(params) => <TextField {...params} />}
                    sx={{ marginLeft: "12px" }}
                  />
                </LocalizationProvider>
              </div>
            )}
          </div>
        </div>
        <div
          style={{
            paddingBottom: "24px",
            paddingTop: "24px",
            display: "flex",
            justifyContent: "end",
            paddingRight: "24px",
            boxShadow: "0px -2px 8px 0px rgba(39, 39, 39, 0.15)",
          }}
        >
          <CustomButton
            variant={"outlined"}
            size={"sm"}
            type={"secondary"}
            text={"Clear All"}
            onClick={handleClearAllData}
          />
          <div style={{ marginLeft: "8px" }}>
            <CustomButton
              variant={"contained"}
              size={"sm"}
              type={"secondary"}
              text={"Apply"}
              disabled={isApplyButtonDisabled}
              onClick={handleDownload}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
}
